import { type FC, useCallback, useEffect, useRef, useState } from 'react'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Dialog, DialogOverlay, DialogPortal } from '@/components/ui/dialog'
import { XCloseIcon } from '@/components/ui/icons'
import { ImagePreview } from '@/components/ui/image-preview'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { cn } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'
import type { VisualVettingListingDetails } from '../types'
import { type GalleryPreviewSize, getGalleryImageUrl } from '../utils'

type VettingImage = VisualVettingListingDetails['images'][number]

const FULL_SIZE: GalleryPreviewSize = 'original'

// Already in the browser cache from the sidebar, so it shows instantly while
// the full-size photo is still downloading
const getThumbnailUrl = (photo: VettingImage) =>
	photo.previewUrls?.extraMedium || photo.imageUrl || undefined

interface PhotoTourDialogProps {
	photos: VettingImage[]
	listingId: string
	open: boolean
	onOpenChange: (open: boolean) => void
	initialIndex?: number
}

export const PhotoTourDialog: FC<PhotoTourDialogProps> = ({
	photos,
	open,
	onOpenChange,
	initialIndex = 0,
	listingId
}) => {
	const { t } = useTranslation()
	const [selectedIndex, setSelectedIndex] = useState(initialIndex)
	const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([])
	const requestedUrlsRef = useRef(new Set<string>())
	const [loadedUrls, setLoadedUrls] = useState<ReadonlySet<string>>(
		() => new Set()
	)

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
				e.preventDefault()
				setSelectedIndex((prev) =>
					prev < photos.length - 1 ? prev + 1 : prev
				)
			} else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
				e.preventDefault()
				setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev))
			}
		},
		[photos.length]
	)

	useEffect(() => {
		if (!open) {
			return
		}

		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [open, handleKeyDown])

	useEffect(() => {
		thumbnailRefs.current[selectedIndex]?.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest'
		})
	}, [selectedIndex])

	// Download the current photo and its neighbours in full size ahead of time,
	// so arrowing through the tour doesn't wait on the network
	useEffect(() => {
		const nearbyPhotos = photos.slice(
			Math.max(0, selectedIndex - 1),
			selectedIndex + 2
		)

		for (const photo of nearbyPhotos) {
			const url = getGalleryImageUrl(photo, FULL_SIZE)
			if (!url || requestedUrlsRef.current.has(url)) {
				continue
			}

			requestedUrlsRef.current.add(url)
			const image = new Image()
			image.onload = () => setLoadedUrls((prev) => new Set(prev).add(url))
			image.src = url
		}
	}, [photos, selectedIndex])

	const currentPhoto = photos[selectedIndex]
	const currentFullUrl =
		currentPhoto && getGalleryImageUrl(currentPhoto, FULL_SIZE)
	const currentSrc =
		currentFullUrl && loadedUrls.has(currentFullUrl)
			? currentFullUrl
			: (currentPhoto && getThumbnailUrl(currentPhoto)) || currentFullUrl

	return (
		<Dialog open={open}>
			<DialogPortal>
				<DialogOverlay className='bg-grey-400' type='light' />

				<div className='fixed inset-0 z-50 grid h-full grid-cols-[1fr_212px] pl-8'>
					{/* header (left) */}
					<div className='grid grid-rows-[auto_1fr] overflow-hidden border-neutral-400 border-r'>
						<div className='flex flex-col pt-12'>
							<ButtonLegacy
								className='absolute top-3 left-6 cursor-pointer p-2'
								onClick={() => onOpenChange(false)}
								shape='square'
								type='button'
								variant='tertiary'
							>
								<XCloseIcon size={16} />
							</ButtonLegacy>
							<div className='flex flex-col gap-1 py-4'>
								<span className='font-semibold text-base'>
									{t('luxe_submissions:listing', {
										listingId
									})}
								</span>
								<span className='text-neutral-700 text-xs'>
									{photos.length}{' '}
									{t('common:images').toLocaleLowerCase()}
								</span>
							</div>
						</div>
						{/* main content */}
						<div className='flex items-center justify-center overflow-hidden p-4'>
							{currentPhoto ? (
								<ImagePreview
									alt={`Listing ${selectedIndex + 1}`}
									className='h-full min-h-100 max-w-full rounded-xl border border-neutral-black/20 object-contain'
									height='100%'
									src={currentSrc}
									width='auto'
								/>
							) : (
								<div className='flex items-center justify-center text-neutral-400'>
									{t('luxe_submissions:no_photo_available')}
								</div>
							)}
						</div>
					</div>

					{/* sidebar */}
					<ScrollArea
						scrollbar={<Scrollbar />}
						style={{ overflow: 'hidden', height: '100%' }}
					>
						<div className='p-6'>
							{/* TODO: restore grouping by amenityKeyName when BE adds it */}
							<div className='flex flex-col gap-4'>
								{photos.map((photo, index) => (
									<button
										className={cn(
											'mx-1 my-1 shrink-0 overflow-hidden rounded-md border border-neutral-black/20 transition-colors',
											index === selectedIndex &&
												'border-2 border-neutral-white shadow-[0_0_0_2px_#000]'
										)}
										key={photo.imageUrl ?? `photo-${index}`}
										onClick={() => setSelectedIndex(index)}
										ref={(el) => {
											thumbnailRefs.current[index] = el
										}}
										type='button'
									>
										<ImagePreview
											alt={`Thumbnail ${index + 1}`}
											className='aspect-video size-full object-cover'
											height='100%'
											src={getThumbnailUrl(photo)}
											width='100%'
										/>
									</button>
								))}
							</div>
						</div>
					</ScrollArea>
				</div>
			</DialogPortal>
		</Dialog>
	)
}
